import { getCountries } from 'libphonenumber-js/min'
import type { CountryCode } from 'libphonenumber-js/min'
import { TranslationType } from '@/types/common'
import { getDialCode, toCountryCode } from '@/utils/phone'
import { COUNTRIES, LOCALIZED_COUNTRIES } from './countries'

export interface IPhoneCountry {
  code: CountryCode
  dialCode: string
  name: TranslationType
}

const getEnglishName = (code: CountryCode): string | undefined =>
  LOCALIZED_COUNTRIES[code as keyof typeof LOCALIZED_COUNTRIES]?.en ?? COUNTRIES[code as keyof typeof COUNTRIES]

const getCountryName = (code: CountryCode): TranslationType | undefined =>
  LOCALIZED_COUNTRIES[code as keyof typeof LOCALIZED_COUNTRIES] ?? COUNTRIES[code as keyof typeof COUNTRIES]

const toPhoneCountry = (code: CountryCode): IPhoneCountry | undefined => {
  const name = getCountryName(code)
  if (!name) return undefined
  return { code, dialCode: getDialCode(code), name }
}

const isPhoneCountry = (country: IPhoneCountry | undefined): country is IPhoneCountry => !!country

/**
 * Built on first use, not at import. Mapping and collating ~250 countries is
 * wasted work for the configured case — one country, no selector — and a
 * top-level computation is also a module side effect, which is what stops a
 * bundler dropping this file from a route that never renders a phone field.
 */
let allPhoneCountries: IPhoneCountry[] | undefined

const getAllPhoneCountries = (): IPhoneCountry[] => {
  allPhoneCountries ??= getCountries()
    .map(toPhoneCountry)
    .filter(isPhoneCountry)
    .sort((a, b) => (getEnglishName(a.code) ?? '').localeCompare(getEnglishName(b.code) ?? ''))
  return allPhoneCountries
}

/**
 * Resolves the configured country list. The caller's order is preserved so the
 * cinematic decides what sits at the top of the selector; an omitted list falls
 * back to every country the library supports.
 */
export const getPhoneCountries = (codes?: string[]): IPhoneCountry[] => {
  if (!codes?.length) return getAllPhoneCountries()

  const configured = codes
    .map(toCountryCode)
    .filter((code): code is CountryCode => !!code)
    .map(toPhoneCountry)
    .filter(isPhoneCountry)

  // A list that is entirely typos would otherwise render a selector with no
  // options and no way to enter a number at all.
  return configured.length ? configured : getAllPhoneCountries()
}
